// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME } from "../../../../../../../core/constants/className";
import { InventoryState } from "../../../../../../../../../io/hyperify/core/store/types/inventory/InventoryState";
import { createDomainInventoryData, isDomainInventoryData, DomainInventoryData } from "../../../../../../../../../io/hyperify/core/store/types/inventory/data/DomainInventoryData";
import { Form } from "../../../../../../../../../io/hyperify/frontend/components/form/Form";
import { TextField } from "../../../../../../../../../io/hyperify/frontend/components/fields/text/TextField";
import { useCallback, useState } from "react";
import { Button } from "../../../../../../../../../io/hyperify/frontend/components/button/Button";
import { SubmitButton } from "../../../../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { InventoryData } from "../../../../../../../../../io/hyperify/core/store/types/inventory/data/InventoryData";
import { LogService } from "../../../../../../../../../io/hyperify/core/LogService";
import { PermissionList } from "../../../../../../../../../io/hyperify/core/PermissionUtils";
import { SendanorApiPermission } from "../../../../../../../core/types/SendanorApiPermission";
import { SelectField } from "../../../../../../../../../io/hyperify/frontend/components/fields/select/SelectField";
import { SelectFieldItem } from "../../../../../../../../../io/hyperify/frontend/types/items/SelectFieldModel";
import { ButtonStyle } from "../../../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import "./MyDomainInventoryDataForm.scss";

const LOG = LogService.createLogger('MyDomainInventoryDataForm');

export interface InventorySubmitCallback {
    (data: InventoryData, state: InventoryState) : void;
}

export interface MyDomainInventoryDataFormProps {
    readonly className            ?: string;
    readonly value                 : InventoryData;
    readonly incorrectDataLabel    : string;
    readonly stateLabel            : string;
    readonly nameLabel             : string;
    readonly resetLabel            : string;
    readonly saveLabel             : string;
    readonly approveLabel          : string;
    readonly state                 : InventoryState;
    readonly save               ?: InventorySubmitCallback;
    readonly permissions : PermissionList<SendanorApiPermission>;
    readonly stateValues : readonly SelectFieldItem<InventoryState>[];
}

export function MyDomainInventoryDataForm (props: MyDomainInventoryDataFormProps) {
    const className = props?.className;
    const stateValues = props?.stateValues;
    const incorrectDataLabel = props?.incorrectDataLabel;
    const nameLabel = props?.nameLabel;
    const stateLabel = props?.stateLabel;
    const submit = props?.save;
    const permissions = props?.permissions;
    const resetLabel = props?.resetLabel;
    const saveLabel = props?.saveLabel;
    const approveLabel = props?.approveLabel;
    const origState = props?.state ?? InventoryState.UNINITIALIZED;
    const isDraft = [InventoryState.UNINITIALIZED, InventoryState.DRAFT].includes(origState);
    const isWaitingApproval = InventoryState.WAITING_APPROVAL === origState;
    const rawValue = props?.value;
    const value : DomainInventoryData | undefined = (
        isDomainInventoryData(rawValue)
            ? rawValue
            : (
                isDraft
                    ? createDomainInventoryData(
                        ''
                    )
                    : undefined
            )
    );
    const canDraftInventoryItems = permissions.includes(SendanorApiPermission.DRAFT_INVENTORY_ITEM);
    const canApproveInventoryItems = permissions.includes(SendanorApiPermission.APPROVE_INVENTORY_ITEM);
    const canResolveInventoryItems = permissions.includes(SendanorApiPermission.RESOLVE_INVENTORY_ITEM);

    const canModifyState = !!submit && canResolveInventoryItems;
    const canModifyName = !!submit && canResolveInventoryItems;
    const canModifyAuthId = !!submit && ( (isDraft && canDraftInventoryItems) || canResolveInventoryItems );

    const isStateReadonly = !(canModifyState);
    const isNameReadonly = !(canModifyName);

    const canBeModified = (
        canModifyState
        || canModifyAuthId
        || canModifyName
    );
    const isReadonly = !canBeModified;
    const origName = value?.name ?? '';
    const [state, setState] = useState<InventoryState>(origState);
    const [name, setName] = useState<string>(origName);
    const isChanged: boolean = (
        state !== origState
        || name !== origName
    );
    LOG.debug(`isChanged = `, isChanged, state, origState, name, origName);
    const isValid : boolean = true;
    const saveEnabled : boolean = !isReadonly && isValid && isChanged;
    const submitDraftEnabled : boolean = !isReadonly && isValid && canDraftInventoryItems && isDraft;
    const approveEnabled : boolean = !isReadonly && isValid && canApproveInventoryItems && isDraft;
    const changeState = useCallback(
        (value: InventoryState) => setState(value),
        [
            setState
        ]
    );
    const changeName = useCallback(
        (value: string|undefined) => setName(value ?? ''),
        [
            setName
        ]
    );
    const clearCallback = useCallback(
        () => {
            setState(() => origState);
            setName(() => origName);
        },
        [
            setState,
            setName,
            origState,
            origName
        ]
    );

    const saveCallback = useCallback(
        () => {
            if (submit) {
                const newItemData : InventoryData = {
                    ...value,
                    name
                };
                submit(newItemData, state);
            } else {
                LOG.error(`saveCallback: Warning! No submit callback defined`);
            }
        },
        [
            value,
            state,
            submit,
            name
        ]
    );

    const approveCallback = useCallback(
        () => {
            if (submit) {
                const newItemData : InventoryData = {
                    ...value,
                    name
                };
                submit(newItemData, [InventoryState.UNINITIALIZED, InventoryState.DRAFT, InventoryState.WAITING_APPROVAL].includes(state) ? InventoryState.CHANGING : state);
            } else {
                LOG.error(`saveCallback: Warning! No submit callback defined`);
            }
        },
        [
            value,
            submit,
            state,
            name
        ]
    );

    const submitDraftCallback = useCallback(
        () => {
            if (submit) {
                const newItemData : InventoryData = {
                    ...value,
                    name
                };
                submit(newItemData, [InventoryState.UNINITIALIZED, InventoryState.DRAFT, InventoryState.WAITING_APPROVAL].includes(state) ? InventoryState.WAITING_APPROVAL : state);
            } else {
                LOG.error(`saveCallback: Warning! No submit callback defined`);
            }
        },
        [
            value,
            submit,
            state,
            name
        ]
    );

    return (
        <div className={
            MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME
            + (className? ` ${className}` : '')
        }>{value === undefined ? (
            incorrectDataLabel
        ): (
            <Form submit={saveCallback}
                  className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form'}
            >
                {canModifyState && !isStateReadonly? (
                    <SelectField
                        label={stateLabel}
                        value={state}
                        values={stateValues}
                        change={changeState}
                    />
                ): null}

                <TextField
                    className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME + '-form-name'}
                    label={nameLabel}
                    value={name}
                    {...(isNameReadonly ? {} : {change:changeName})}
                />

                {isReadonly ? null : (
                    <div className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form-buttons'}>
                        <Button
                            className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form-buttons-clear'}
                            click={clearCallback}>{resetLabel}</Button>

                        {canResolveInventoryItems ? (
                            <SubmitButton
                                className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form-buttons-submit'}
                                enabled={saveEnabled}
                            >{saveLabel}</SubmitButton>
                        ) : (
                            <>
                                {isDraft && canDraftInventoryItems ? (
                                    <Button
                                        className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form-buttons-submit'}
                                        style={ButtonStyle.PRIMARY}
                                        enabled={submitDraftEnabled}
                                        click={submitDraftCallback}
                                    >{saveLabel}</Button>
                                ): null}
                            </>
                        )}
                        {isWaitingApproval && canApproveInventoryItems ? (
                            <Button
                                className={MY_DOMAIN_INVENTORY_DATA_FORM_CLASS_NAME+'-form-buttons-submit'}
                                enabled={approveEnabled}
                                click={approveCallback}
                            >{approveLabel}</Button>
                        ): null}
                    </div>
                )}
            </Form>
        )}</div>
    );
}
